import posthog from 'posthog-js'
import axiosInstance from '../../axios/axiosInstance'

/**
 * "Report this meaning" (docs/plans/localization.md, "When translations are made"): a learner
 * flags a learner-language translation. Reported items are regenerated and rechecked by the
 * backend. `ref_id` follows the stored translations' scheme ("Stored translations"):
 *   word     -> the Chinese word (simplified)
 *   sentence -> '<storyId>:<paragraphIndex>:<sentenceIndex>'
 */

export type ReportKind = 'word' | 'sentence'

export interface TranslationReport {
  kind: ReportKind
  ref_id: string
  lang: string
  note?: string
}

/** The body for POST translations/report. An empty note is left out. */
export const reportPayload = ({ kind, ref_id, lang, note }: TranslationReport): TranslationReport => {
  const trimmed = (note || '').trim()
  return trimmed ? { kind, ref_id, lang, note: trimmed } : { kind, ref_id, lang }
}

/**
 * Sends a report. Never throws: the endpoint may not be deployed yet (404), and a report is never
 * worth an error in the learner's face. Resolves to whether the server took it.
 */
export const sendTranslationReport = async (report: TranslationReport): Promise<boolean> => {
  try {
    await axiosInstance.post('translations/report', reportPayload(report))
    return true
  } catch {
    return false
  }
}

/** PostHog `meaning_reported`, once per report (not again for the optional note). */
export const trackMeaningReported = (kind: ReportKind, lang: string) => {
  try {
    posthog.capture('meaning_reported', { kind, lang })
  } catch {
    /* analytics must never break the reader */
  }
}

/**
 * The ref_id of a story sentence for `kind: 'sentence'`: `<storyId>:<paragraph>:<sentence>`, where
 * the paragraph is `sentenceTranslations[i].paragraph` and the sentence its position among that
 * paragraph's entries. Null when there is no story id or no such entry.
 */
export const sentenceRefId = (
  storyId: string | null | undefined,
  sentenceTranslations: { paragraph?: number | string | null }[] | null | undefined,
  index: number,
): string | null => {
  if (!storyId || !Array.isArray(sentenceTranslations) || !sentenceTranslations[index]) return null
  const paragraphOf = (entry: { paragraph?: number | string | null } | undefined) => Number(entry?.paragraph) || 0
  const paragraph = paragraphOf(sentenceTranslations[index])
  let position = 0
  for (let i = 0; i < index; i++) {
    if (paragraphOf(sentenceTranslations[i]) === paragraph) position++
  }
  return `${storyId}:${paragraph}:${position}`
}

/**
 * The `language_story` _id (hex) a story payload carries, which sentence ref_ids are built from.
 * The reader's `id` is the Prismic id, which is NOT it. Null when the payload has none.
 */
export const languageStoryId = (story: any): string | null => {
  const candidates = [story?.languageStoryId, story?.language_story_id, story?._id]
  const found = candidates.find((value) => typeof value === 'string' && /^[0-9a-f]{24}$/i.test(value))
  return found || null
}
